import React from "react";
import { Row, Col, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FaInfoCircle } from "react-icons/fa";
import {
  getOperatorsForField,
  isOperatorValueRequired,
  isPicklistField,
  isBooleanField,
  isMultipleValuesAllowed,
  getFieldByName,
} from "../../utils/conditionBuilderUtils";
import {
  standardInputStyles,
  standardLabelStyles,
  standardInputContainerHeight,
} from "../../utils/conditionInputStyles";
import PicklistFieldSelector from "../workflow/PicklistFieldSelector";
import FieldSelectorDropdown from "./FieldSelectorDropdown";
import LookupLayout from "../layout/LookupLayout";

/**
 * Reusable Condition Row Component
 * Displays Field-Operator-Value inputs with proper filtering and value types
 *
 * @param {Object} props
 * @param {Object} condition - Condition object with fieldName/field, operator, value
 * @param {Array} fields - Array of available fields
 * @param {Function} onChange - Callback when condition changes (index, field, value)
 * @param {number} index - Index of the condition
 * @param {boolean} readOnly - Whether inputs are disabled
 * @param {Object} options - Additional options:
 *   - fieldKey: Key name for field in condition object (default: 'fieldName' or 'field')
 *   - valueKey: Key name for value in condition object (default: 'value')
 *   - operatorKey: Key name for operator in condition object (default: 'operator')
 *   - nameKey: Key name for field name in fields array (default: 'field_name')
 *   - labelKey: Key name for field label in fields array (default: 'field_label')
 *   - customOperators: Custom operators array
 *   - picklistOperators: Custom picklist operators array
 *   - showFieldLabel: Show field label dropdown (default: true)
 *   - fieldColSize: Column size for field (default: 3)
 *   - operatorColSize: Column size for operator (default: 2)
 *   - valueColSize: Column size for value (default: 3)
 */
export default function ConditionRow({
  condition,
  fields = [],
  onChange,
  index,
  readOnly = false,
  options = {},
  /** Optional map fieldId -> [{ key, value }] for prefetched picklist options (list view filters). */
  picklistPrefetchByFieldId = null,
}) {
  // Extract options with defaults
  const {
    fieldKey = condition.fieldName !== undefined ? 'fieldName' : 'field',
    valueKey = 'value',
    operatorKey = 'operator',
    nameKey = 'field_name',
    labelKey = 'field_label',
    customOperators,
    picklistOperators,
    showFieldLabel = true,
    fieldColSize = 3,
    operatorColSize = 2,
    valueColSize = 3,
    useSearchableDropdown = true, // New option to use searchable dropdown
    fieldTypeFilter = null, // Optional field type filter
    fieldFilter = null, // Optional custom field filter function
    hideSubtitle = false,
    subtitleKey = null,
  } = options;

  // Get current field name and field object
  const fieldName = condition[fieldKey] || '';
  const selectedField = getFieldByName(fields, fieldName, { nameKey });

  // Get operators for the selected field
  const operators = getOperatorsForField(selectedField, {
    customOperators,
    picklistOperators,
  });

  // Check if value is required for the operator
  const valueRequired = isOperatorValueRequired(condition[operatorKey] || '');

  // Check if multiple values are allowed
  const allowMultiple = isMultipleValuesAllowed(selectedField, condition[operatorKey] || '');

  // Check if field is a lookup/reference field
  const isLookupField = selectedField && (
    selectedField.data_type === 'lookup' ||
    selectedField.data_type === 'reference' ||
    selectedField.is_reference === true
  ) && (selectedField.reference_object || selectedField.referenceObject);

  // Extract lookup filter similar to EditLayout
  const extractLookupFilter = (field) => {
    let lookupFilterArray = null;
    let lookupFilterLogic = 'AND';

    if (field?.lookupFilter || field?.lookup_filter) {
      const lookupFilter = field.lookupFilter || field.lookup_filter;

      if (typeof lookupFilter === 'string') {
        try {
          const parsed = JSON.parse(lookupFilter);
          if (typeof parsed === 'object' && !Array.isArray(parsed) && parsed.filters) {
            lookupFilterArray = Array.isArray(parsed.filters) ? parsed.filters : null;
            lookupFilterLogic = parsed.logic && (parsed.logic.toUpperCase() === 'OR' || parsed.logic.toUpperCase() === 'AND')
              ? parsed.logic.toUpperCase()
              : 'AND';
          } else if (Array.isArray(parsed)) {
            lookupFilterArray = parsed;
            lookupFilterLogic = 'AND';
          }
        } catch (e) {
          console.error('Failed to parse lookup_filter:', e);
        }
      } else if (typeof lookupFilter === 'object' && !Array.isArray(lookupFilter) && lookupFilter.filters) {
        lookupFilterArray = Array.isArray(lookupFilter.filters) ? lookupFilter.filters : null;
        lookupFilterLogic = lookupFilter.logic && (lookupFilter.logic.toUpperCase() === 'OR' || lookupFilter.logic.toUpperCase() === 'AND')
          ? lookupFilter.logic.toUpperCase()
          : 'AND';
      } else if (Array.isArray(lookupFilter)) {
        lookupFilterArray = lookupFilter;
        lookupFilterLogic = field.lookup_filter_logic || 'AND';
      }
    }

    return { lookupFilterArray, lookupFilterLogic };
  };

  // Handle field change
  const handleFieldChange = (newFieldName) => {
    onChange(index, fieldKey, newFieldName);

    // Reset operator and value when field changes
    if (operators.length > 0) {
      onChange(index, operatorKey, operators[0].value);
    } else {
      onChange(index, operatorKey, '');
    }
    onChange(index, valueKey, '');
    onChange(index, 'displayValue', '');
  };

  // Handle operator change
  const handleOperatorChange = (newOperator) => {
    const oldOperator = condition[operatorKey] || '';
    const wasMultiple = isMultipleValuesAllowed(selectedField, oldOperator);
    const isMultiple = isMultipleValuesAllowed(selectedField, newOperator);

    // Clear value when switching from multiple to single selection
    if (wasMultiple && !isMultiple) {
      onChange(index, valueKey, '');
      onChange(index, 'displayValue', '');
    }

    onChange(index, operatorKey, newOperator);
  };

  // Handle value change
  const handleValueChange = (newValue) => {
    onChange(index, valueKey, newValue);
  };

  return (
    <Row className="align-items-end">
      {/* Field Name Selector */}
      <Col md={fieldColSize}>
        <Form.Group className="mb-3">
          <Form.Label style={standardLabelStyles}>Field Name</Form.Label>
          {useSearchableDropdown ? (
            <div style={standardInputContainerHeight}>
              <FieldSelectorDropdown
                fields={fields}
                value={fieldName}
                onChange={handleFieldChange}
                nameKey={nameKey}
                labelKey={labelKey}
                placeholder="-Select Field-"
                disabled={readOnly}
                size=""
                fieldTypeFilter={fieldTypeFilter}
                fieldFilter={fieldFilter}
                hideSubtitle={hideSubtitle}
                subtitleKey={subtitleKey}
              />
            </div>
          ) : (
            <Form.Select
              value={fieldName}
              onChange={(e) => handleFieldChange(e.target.value)}
              disabled={readOnly}
              style={standardInputStyles}
            >
              <option value="">-Select Field-</option>
              {fields.map((field) => {
                const fieldValue = field[nameKey] || field.name || '';
                const fieldLabel = field[labelKey] || field.label || field.field_name || fieldValue;
                return (
                  <option key={fieldValue} value={fieldValue}>
                    {fieldLabel}
                  </option>
                );
              })}
            </Form.Select>
          )}
        </Form.Group>
      </Col>

      {/* Operator Selector */}
      <Col md={operatorColSize}>
        <Form.Group className="mb-3">
          <Form.Label style={standardLabelStyles}>Operator</Form.Label>
          <Form.Select
            value={condition[operatorKey] || ''}
            onChange={(e) => handleOperatorChange(e.target.value)}
            disabled={readOnly || !fieldName}
            style={standardInputStyles}
          >
            <option value="">Select</option>
            {operators.map((op) => {
              const opValue = typeof op === 'string' ? op : op.value;
              const opLabel = typeof op === 'string' ? op : op.label;
              return (
                <option key={opValue} value={opValue}>
                  {opLabel}
                </option>
              );
            })}
          </Form.Select>
        </Form.Group>
      </Col>

      {/* Value Input */}
      <Col md={valueColSize}>
        <Form.Group className="mb-3">
          <Form.Label style={standardLabelStyles}>
            Value
            {isPicklistField(selectedField) && allowMultiple && (
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="multipicklist-tooltip">
                    Select multiple values. Values will be joined with semicolons.
                  </Tooltip>
                }
              >
                <FaInfoCircle
                  className="ms-1 text-muted"
                  style={{ cursor: 'help', fontSize: '0.875rem' }}
                />
              </OverlayTrigger>
            )}
          </Form.Label>
          {isPicklistField(selectedField) ? (
            <div style={standardInputContainerHeight}>
              <PicklistFieldSelector
                field={selectedField}
                value={condition[valueKey] || ''}
                onChange={handleValueChange}
                disabled={readOnly || !valueRequired}
                isMultiSelect={allowMultiple}
                prefetchedPicklistOptions={
                  picklistPrefetchByFieldId != null && selectedField?.id != null
                    ? picklistPrefetchByFieldId[selectedField.id]
                    : undefined
                }
              />
            </div>
          ) : isBooleanField(selectedField) ? (
            <Form.Select
              value={condition[valueKey] || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              disabled={readOnly || !valueRequired}
              style={standardInputStyles}
            >
              <option value="">-Select-</option>
              <option value="true">True</option>
              <option value="false">False</option>
            </Form.Select>
          ) : isLookupField ? (
            (() => {
              const { lookupFilterArray, lookupFilterLogic } = extractLookupFilter(selectedField);
              // Get display value from condition if available (from backend)
              const displayLabel = condition.displayValue || '';
              return (
                <div style={standardInputContainerHeight}>
                  <LookupLayout
                    objectName={selectedField.reference_object || selectedField.referenceObject}
                    searchFields={selectedField.lookup_search_fields || selectedField.lookupSearchFields || 'name'}
                    displayFields={selectedField.display_fields || selectedField.displayFields || 'name'}
                    value={condition[valueKey] || ''}
                    defaultLabel={displayLabel}
                    onChange={(id, label) => {
                      const nextId = id != null && String(id).trim() !== '' ? id : '';
                      handleValueChange(nextId);
                      onChange(
                        index,
                        'displayValue',
                        label != null && String(label).trim() !== '' ? label : ''
                      );
                    }}
                    disabled={readOnly || !valueRequired}
                    placeholder={valueRequired ? "Search..." : "N/A"}
                    isRequired={valueRequired}
                    lookupFilter={lookupFilterArray}
                    lookupFilterLogic={lookupFilterLogic}
                  />
                </div>
              );
            })()
          ) : (
            <Form.Control
              type="text"
              value={condition[valueKey] || ''}
              onChange={(e) => handleValueChange(e.target.value)}
              disabled={readOnly || !valueRequired}
              placeholder={valueRequired ? "Enter value" : "N/A"}
              style={standardInputStyles}
            />
          )}
        </Form.Group>
      </Col>
    </Row>
  );
}
